import { Component, OnInit, HostListener, Inject, forwardRef } from '@angular/core';
import { RoleService } from '../service/role.service';
import { CognitoService } from '../service/cognito.service';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '../local-storage.service';
import { ClientService } from '../service/client.service';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ValidationService } from '../service/validation.service';
import { Router } from '@angular/router';
import { firstValueFrom, take, timer } from 'rxjs';
import { ThemeService } from '../service/theme.service';
import { NavigationService } from '../service/navigation.service';
import { SystemMessageService } from '../service/system-message.service';
import { environment } from '../environments/environment';


@Component({
    selector: 'app-client-create',
    templateUrl: './client-create.component.html',
    styleUrls: ['./client-create.component.css'],
    standalone: false
})
export class ClientCreateComponent implements OnInit{

checkboxValue: number = 0;
public billableValue: number = 0;

public form: FormGroup

public successMessage: boolean = false;

public autocomplete: any = google.maps.places; // Google Places Autocomplete

public isButtonDisabled: boolean = false;

// Variable to store the result of a place selection from Google Places Autocomplete.
// It can hold a valid PlaceResult object or be undefined if no place is selected.
selectedPlace: google.maps.places.PlaceResult | undefined;

  constructor(
    private localStorageService: LocalStorageService,
    @Inject(forwardRef(() => TranslateService)) @Inject(forwardRef(() => TranslateService)) private translate: TranslateService,
    private cognitoService: CognitoService,
    public roleService: RoleService,
    public clientService: ClientService,
    private formBuilder: FormBuilder,
    public validationService: ValidationService,
    private router: Router,
    public theme: ThemeService,
    public navigationService: NavigationService,
    public systemMessage: SystemMessageService

  ) {

    // Create a form using the FormBuilder
    this.form = this.formBuilder.group({
      // Define form controls and their initial values
      client_name: ['', Validators.required],
      legal_name: [''],
      email: ['', [Validators.required, Validators.email]],
      phone_number: [''],
      address: ['']
    });

    this.cognitoService.confirmValidUser();
    this.roleService.getRoles();
  }

  async ngOnInit() {

    this.isButtonDisabled = false;
    this.systemMessage.buttonBlocked = false;

    // Check cognito user for valid user and get role(s) of the user
    this.cognitoService.confirmValidUser();
    await this.roleService.getRoles();

    // Check if user have the roles to get in this page
    this.cognitoService.getCurrentRole([environment.users.role.administrator], [environment.users.superAdmin], true, this.roleService.roles);

    this.clearPastClientData();
    this.initAutocomplete();
  }

  disableButton() {
    this.isButtonDisabled = true;
    this.systemMessage.buttonBlocked = true;
  }

  // Function called to clear all client datas
  clearPastClientData() {
    this.clientService.clientData.client_name = "";
    this.clientService.clientData.client_id = "";
    this.clientService.clientData.address = "";
    this.clientService.clientData.created = "";
    this.clientService.clientData.email = "";
    this.clientService.clientData.legal_name = "";
    this.clientService.clientData.phone_number = "";
    this.clientService.clientData.modified = "";
    this.clientService.clientData.billable = 0;
    this.clientService.clientData.billable_timestamp = null;
    this.billableValue = 0;
  }

  // Decorator @HostListener listens to the 'input' event on the specified target element
  @HostListener('input', ['$event.target']) onInput(input: HTMLInputElement): void {
    // Check if the input element has the class 'telefone-input'
    if (input.classList.contains('telefone-input')) {
      // Remove all non-numeric characters from the input value
      const value = input.value.replace(/\D/g, '');

      // Check if the value has a length of 10 characters or less
      if (value.length <= 10) {
        // Format the phone number as (000) 000-0000
        input.value = this.formatPhoneNumber(value);
      } else {
        // If the value is longer than 10 digits, limit the input to 10 digits
        input.value = input.value.slice(0, 10);
      }
    }
  }

  // runs a validation test checking every inputs
  checkValidationAllInputs() {

    // Check if clientName is not empty, its length is greater than 16,
    // or it doesn't match the alphanumeric pattern
    this.validationService.validateNameInput(this.clientService.clientData.client_name, 'username');

    // Check if clientLegalName is not empty, its length is greater than 16,
    // or it doesn't match the alphanumeric pattern
    this.validationService.validateNameInput(this.clientService.clientData.legal_name, 'userclientLegalName');

    // Check if the clientEmail is not a valid email or is empty
    this.validationService.validateClientEmail(this.clientService.clientData.email);
    

    // Check if the address is empty
    this.validationService.validateAddress(this.clientService.clientData.address);

    // Define a mapping of error codes to corresponding error messages
    const errorMappings: Record<string, string> = {
      'usernameInvalid': 'invalidNameError',
      'clientLegalNameInvalid': 'invalidLegalNameError',
      'clientEmailInvalid': 'invalidEmail',
      'clientAddressInvalid': 'invalidAddress',
    };

    // Retrieve the first validation error from the array
    const validationError = this.validationService.validationErrorArray[0];

    // Check if the validation error code exists in the mapping
    if (errorMappings[validationError]) {
      this.systemMessage.buttonBlocked = true;

      // If so, display a danger ribbon message with the corresponding key
      this.systemMessage.selectRibbon('danger', errorMappings[validationError]);
    } else {
      console.error('An error occurred, please contact support');
    }
  }

  // Function triggered when the "Ok" button is clicked
  createBtn() {
    this.checkValidationAllInputs();

    // Create a new Date object to get the current date and time
    const date = new Date();

    // Format the date using the validationService
    this.validationService.formatDate(date);

    // Remove non-numeric characters from the phone number using the validationService
    this.validationService.keepOnlyNumbers(this.clientService.clientData.phone_number);

    // Update the client's phone number and creation date with the formatted values
    this.clientService.clientData.phone_number = this.validationService.phoneNumberFixed;
    this.clientService.clientData.created = this.validationService.dateFixed;
    this.clientService.clientData.modified = this.validationService.dateFixed;

    // Billable timestamp is set only when creating a billable client.
    const billable = this.billableValue === 1 ? 1 : 0;
    const billableTimestamp = billable === 1 ? this.getUnixTimestamp() : null;
    this.clientService.clientData.billable = billable;
    this.clientService.clientData.billable_timestamp = billableTimestamp;

    if(this.validationService.clientNameValid &&
      this.validationService.clientLegalNameValid &&
      this.validationService.clientEmailValid &&

      this.validationService.clientAddressValid ) {
        // If all validations pass, log a message and call the createClient function
        firstValueFrom(this.clientService.createClient())
          .then((result) => {
            this.localStorageService.addItem('entitiesContent', 'clients');
            this.router.navigate(['/entities']);
            this.successMessage = true;
            this.validationService.clientCreateSuccessMessage = true;
          })
          .catch((error) => {
            console.error('error:', error);
          });
      } else {
        console.error("Invalid Inputs");
      }
      this.isButtonDisabled = false;
      this.systemMessage.buttonBlocked = false;
  }

  private getUnixTimestamp(): number {
    return Math.floor(Date.now() / 1000);
  }

  // function to reload page when cancel button is clicked
  pageReload() {
    this.localStorageService.addItem('entitiesContent', 'clients')
    // this.navigationService.entitiesContent = "clients"
    // window.location.reload();
    this.router.navigate(['/entities']); // Redirect to the '/client-list' route after the delay
  }

  // Function to format a numeric string as a phone number in the format (000) 000-0000
  formatPhoneNumber(value: string): string {
    // Check if the input value is empty and return an empty string if it is
    if (value.length === 0) return '';

    // Extract the area code, first part, and second part of the phone number
    const areaCode = value.slice(0, 3);
    const firstPart = value.slice(3, 6);
    const secondPart = value.slice(6, 10);

    // Format the phone number as (000) 000-0000 and return the formatted string
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  }

  ///// *************** This function should be in localization service ****************** /////
  // Function to initialize the Google Maps Places Autocomplete
  initAutocomplete() {
    // Ensure that the Google Maps API script is loaded before calling this function
    if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
      throw new Error('Google Maps API is not loaded.');
    }

    // Get the input element with the ID 'autocomplete'
    const inputElement = document.getElementById('autocomplete') as HTMLInputElement;

    // Check if the input element was found
    if (!inputElement) {
      throw new Error('"autocomplete" input element not found.');
    }

    // Set options for the Autocomplete widget
    const autocompleteOptions: google.maps.places.AutocompleteOptions = {
      // Specify the types of places to search for (geocode in this case)
      types: ['geocode'],
      // Restrict results to Canada
      componentRestrictions: { country: 'ca' },
      // Define the fields to be retrieved from place details
      fields: ['place_id', 'geometry', 'name', 'formatted_address'],
    };

    // Create a new instance of the Autocomplete widget and associate it with the input element
    this.autocomplete = new google.maps.places.Autocomplete(inputElement, autocompleteOptions);

    // Add a listener to handle the selection of a place in the Autocomplete results
    this.autocomplete.addListener('place_changed', () => {

      // Variable to store the result of a place selection from Google Places Autocomplete.
      // It can hold a valid PlaceResult object or be undefined if no place is selected.
      const selectedPlace = this.autocomplete.getPlace();


      if (selectedPlace && selectedPlace.geometry) {
        let formattedAddress = selectedPlace.formatted_address;

        // Check if the 'formatted_address' property is available
        if (!formattedAddress && selectedPlace.name) {
          formattedAddress = selectedPlace.name;
        }

        // Check if the 'name' property is available
        if (!formattedAddress && selectedPlace.address_components) {
          // Build the address from address components
          formattedAddress = selectedPlace.address_components.map((component: { long_name: any; }) => component.long_name).join(', ');
        }

        if (formattedAddress) {
          this.clientService.clientData.address = formattedAddress;
        }
      }
    });
  }
}
